// Pure helpers for the part-workflow system. See spec:
// docs/superpowers/specs/2026-05-26-part-workflows-design.md
//
// Override key formats:
//   byTechnology[techUUID]
//   byMaterial[`${techUUID}/${matUUID}`]
//   byColor[`${techUUID}/${matUUID}/${colorUUID}`]
//
// Each more-specific composite must match the part's full (Tech, Material, Color)
// settings — a Color override under FDM/PLA never fires for FDM/PETG, even if the
// color UUID is the same.

export function workflowKey (technologyUUID, materialUUID, colorUUID) {
  return [technologyUUID, materialUUID, colorUUID].filter(Boolean).join('/')
}

export function resolveWorkflow (settings, cfg) {
  if (!settings || !cfg) return cfg && cfg.defaultWorkflow
  const { Technology, Material, Color } = settings
  if (Technology && Material && Color) {
    const k = `${Technology}/${Material}/${Color}`
    if (cfg.byColor && cfg.byColor[k]) return cfg.byColor[k]
  }
  if (Technology && Material) {
    const k = `${Technology}/${Material}`
    if (cfg.byMaterial && cfg.byMaterial[k]) return cfg.byMaterial[k]
  }
  if (Technology && cfg.byTechnology && cfg.byTechnology[Technology]) {
    return cfg.byTechnology[Technology]
  }
  return cfg.defaultWorkflow
}

// Compare two workflows by their ordered stages on (name, color). IDs are intentionally
// excluded — a re-forked catalog workflow has new UUIDs but identical content, and we don't
// want that to look "different". Ignores the top-level workflow `name`, so a legacy part
// (whose workflow is named "Legacy" by convention) reads as in-sync with the resolved default
// when only that name differs but the stages match.
export function workflowStagesDiffer (a, b) {
  const sa = (a && a.stages) || []
  const sb = (b && b.stages) || []
  if (sa.length !== sb.length) return true
  for (let i = 0; i < sa.length; i++) {
    if (sa[i].name !== sb[i].name || sa[i].color !== sb[i].color) return true
  }
  return false
}

// Like workflowStagesDiffer but also treats a top-level `name` change as a difference.
// Drives "Reset to latest" enable state and the workflowCustom flag.
export function workflowsDiffer (a, b) {
  if (((a && a.name) || '') !== ((b && b.name) || '')) return true
  return workflowStagesDiffer(a, b)
}

export function cloneWorkflowForFork (workflow, makeUUID) {
  return {
    ...(workflow.name ? { name: workflow.name } : {}),
    stages: (workflow.stages || []).map(s => ({
      id: makeUUID(),
      name: s.name,
      color: s.color,
    })),
  }
}

// Re-attach a catalog-resolved workflow's stages to a model's existing stage IDs so counts
// survive a "Reset to latest". Positional semantics: the FIRST and LAST stages are anchored
// by position (begun/done); interior stages match by name. Unmatched resolved stages get a
// fresh id; unmatched base ids are simply not reused (their counts orphan downstream).
export function remapWorkflowOntoIds (resolved, baseStages, makeUUID) {
  const base = baseStages || []
  const rStages = (resolved && resolved.stages) || []
  const n = rStages.length
  const firstId = base.length ? base[0].id : null
  const lastId = base.length >= 2 ? base[base.length - 1].id : null
  const interior = base.slice(1, base.length - 1) // excludes both anchors
  const consumed = new Set()
  const stages = rStages.map((s, i) => {
    let id
    if (i === 0 && firstId) {
      id = firstId
    } else if (i === n - 1 && lastId && !consumed.has(lastId)) {
      id = lastId
    } else {
      const match = interior.find(b => b.name === s.name && !consumed.has(b.id))
      id = match ? match.id : makeUUID()
    }
    consumed.add(id)
    return { id, name: s.name, color: s.color }
  })
  return { ...(resolved && resolved.name ? { name: resolved.name } : {}), stages }
}

export function migrateLegacyCounters (workflow, legacy) {
  const counts = {}
  const stages = (workflow && workflow.stages) || []
  if (!stages.length) return counts
  const first = stages[0]
  const last = stages[stages.length - 1]
  const printedTotal = parseInt((legacy && legacy.qtyPrinting) || 0) || 0
  const counted = parseInt((legacy && legacy.qtyCounted) || 0) || 0
  const printedNotCounted = Math.max(0, printedTotal - counted)
  if (printedNotCounted > 0) counts[first.id] = (counts[first.id] || 0) + printedNotCounted
  if (counted > 0) counts[last.id] = (counts[last.id] || 0) + counted
  return counts
}

// Reconcile a {stageId: count} map across a workflow change, keyed by stage ID.
// Surviving ids keep their count; removed ids with count > 0 are reported in `orphaned`
// (named from oldStages) and their parts fall back to not-started. `not-started` is derived
// (quantity − Σ counts), so no quantity argument is needed. The Reset path runs
// remapWorkflowOntoIds first so its newStages share ids with oldStages where appropriate.
export function reconcileCounts (oldCounts, oldStages, newStages) {
  const counts = {}
  const orphaned = []
  const newIds = new Set((newStages || []).map(s => s.id))
  const nameById = {}
  for (const s of (oldStages || [])) nameById[s.id] = s.name
  for (const [id, raw] of Object.entries(oldCounts || {})) {
    const count = parseInt(raw || 0) || 0
    if (newIds.has(id)) {
      if (count) counts[id] = count
    } else if (count > 0) {
      orphaned.push({ id, name: nameById[id] || id, count })
    }
  }
  return { counts, orphaned }
}

export const NOT_STARTED = '__notStarted__'

// Unbounded, non-stored source. Drawing from it adds to a stage without decrementing
// anything and without a quantity clamp — this is how a stage is allowed to exceed
// quantity (e.g. 12/10). Like NOT_STARTED it is derived/never persisted.
export const EXTERNAL = '__external__'

const countOf = (counts, id) => parseInt((counts && counts[id]) || 0) || 0

export function numeratorAt (counts, stages, i) {
  return countOf(counts, stages[i] && stages[i].id)
}

// Denominator depends only on LATER stages (quantity − Σ later), never on this stage's own
// count or earlier ones. Design choice: denominators stay anchored to the ORDER (quantity), so
// over-counting a stage (e.g. 6/4 Printing — see EXTERNAL/planStageSet) only grows that stage's
// numerator and leaves every downstream denominator fixed. We deliberately do NOT float future
// denominators up to absorb the excess: the denominator means "what was ordered", and the
// over-count itself (numerator > denominator) is the overproduction signal — inflating the
// denominator would hide it (Printing would read 6/6 as if complete) and break chip aggregation
// across models. As over-produced parts advance, each stage reads e.g. 6/4 in turn.
export function denominatorAt (counts, stages, i, quantity) {
  let laterSum = 0
  for (let j = i + 1; j < stages.length; j++) laterSum += countOf(counts, stages[j].id)
  return Math.max(0, (parseInt(quantity || 0) || 0) - laterSum)
}

export function notStartedCount (counts, stages, quantity) {
  let sum = 0
  for (const s of stages) sum += countOf(counts, s.id)
  return Math.max(0, (parseInt(quantity || 0) || 0) - sum)
}

// Move up to n parts from fromBucket to toBucket. Buckets are a stageId or NOT_STARTED.
// Clamps so no stage and notStarted goes below 0. Returns a new counts object.
export function transfer (counts, stages, quantity, fromBucket, toBucket, n) {
  const out = { ...(counts || {}) }
  let movable = parseInt(n || 0) || 0
  if (movable <= 0 || fromBucket === toBucket) return out
  const avail = fromBucket === NOT_STARTED
    ? notStartedCount(out, stages, quantity)
    : fromBucket === EXTERNAL
      ? movable // unbounded thin-air source
      : countOf(out, fromBucket)
  movable = Math.min(movable, avail)
  if (movable <= 0) return out
  if (fromBucket !== NOT_STARTED && fromBucket !== EXTERNAL) out[fromBucket] = countOf(out, fromBucket) - movable
  if (toBucket !== NOT_STARTED && toBucket !== EXTERNAL) out[toBucket] = countOf(out, toBucket) + movable
  return out
}

// Index of the nearest stage before `i` that still holds a part, or -1. Skips empty intermediate
// stages, so a part left two stages back is still the source — the same search countOneAt runs.
function nearestOccupiedBefore (counts, stages, i) {
  for (let j = i - 1; j >= 0; j--) {
    if (countOf(counts, stages[j] && stages[j].id) > 0) return j
  }
  return -1
}

// Plan the transfer intents that set stage `i` to `target`, enforcing Σstages ≤ quantity and
// counts ≥ 0. Returns an ordered array of { fromBucket, toBucket, n } (each ready to feed
// transfer()); [] for a no-op. RAISING walks BACKWARDS through the earlier stages, nearest first,
// taking what each holds, and only then draws the shortfall from the unallocated pool
// (NOT_STARTED). LOWERING returns the freed parts to the pool. Amounts are pre-clamped to
// availability, so the intents fully describe the resulting change.
//
// This ladder used to be pool-first, borrowing from stage 0 only once the pool ran dry and only
// for i === 1, on the grounds that a stage with two+ previous stages had no unambiguous source.
// That reads wrong on the counter: with 10 units at 3 Printing / 3 Counted, counting 3 more left
// Printing at 3 and quietly consumed the unstarted pool, as though the parts just counted had
// never been printed. Parts only reach a later stage by passing through the earlier ones, so the
// earlier stages ARE the source, and "which one" is not ambiguous — it is the nearest occupied
// one, exactly what countOneAt below already does (see the cascade fix that made the last stage
// advance in multi-stage workflows). The pool is the bootstrap for parts that never entered the
// workflow at all. Lowering deliberately still returns parts to the pool rather than pushing them
// back one stage.
export function planStageSet (counts, stages, quantity, i, target, { capAtQuantity = false } = {}) {
  if (!Array.isArray(stages) || i < 0 || i >= stages.length) return []
  const hereId = stages[i].id
  const want = Math.max(0, parseInt(target || 0) || 0)
  const current = countOf(counts, hereId)
  const delta = want - current
  if (delta === 0) return []
  if (delta < 0) return [{ fromBucket: hereId, toBucket: NOT_STARTED, n: -delta }]
  const intents = []
  let remaining = delta
  for (let j = i - 1; j >= 0 && remaining > 0; j--) {
    const take = Math.min(remaining, countOf(counts, stages[j].id))
    if (take > 0) {
      intents.push({ fromBucket: stages[j].id, toBucket: hereId, n: take })
      remaining -= take
    }
  }
  // Moving parts between stages leaves Σstages unchanged, so the pool derived from the ORIGINAL
  // counts is still exactly what remains after the walk above.
  const fromPool = Math.min(remaining, notStartedCount(counts, stages, quantity))
  if (fromPool > 0) {
    intents.push({ fromBucket: NOT_STARTED, toBucket: hereId, n: fromPool })
    remaining -= fromPool
  }
  // Any remainder beyond the real earlier stages + real pool is added out of thin air, letting the
  // stage exceed quantity (e.g. 12/10). EXTERNAL persists as a plain +n on hereId. When the
  // enterprise caps counters at quantity, the excess is dropped instead and the raise clamps
  // to what is really available.
  if (remaining > 0 && !capAtQuantity) intents.push({ fromBucket: EXTERNAL, toBucket: hereId, n: remaining })
  return intents
}

// Whether stage `i`'s "+1" control is enabled. Uncapped (the default), counts may exceed
// quantity — the excess is added out of thin air (see planStageSet/EXTERNAL) — so the only
// gate is a valid index. Capped (per-enterprise capStageCountsAtQuantity), this mirrors
// planStageSet's raise rule: the "+1" needs a real source — an earlier stage holding a part,
// or the pool.
export function canIncrementStage (counts, stages, quantity, i, { capAtQuantity = false } = {}) {
  if (!Array.isArray(stages) || i < 0 || i >= stages.length) return false
  if (!capAtQuantity) return true
  if (nearestOccupiedBefore(counts, stages, i) >= 0) return true
  return notStartedCount(counts, stages, quantity) > 0
}

// Advance one part into the LAST stage. Delegates to countOneAt, which cascades from the nearest
// occupied earlier stage (else NOT_STARTED) — so a part sitting two+ stages back (e.g. still in
// Printing with an empty Cooling between it and the end) still advances. No-op if nothing is
// available anywhere.
export function countOneDone (counts, stages, quantity) {
  if (!stages.length) return { ...(counts || {}) }
  return countOneAt(counts, stages, quantity, stages.length - 1)
}

// {stageId: net change} between two counts objects, omitting zero deltas. Keyed by real stage
// ids only (NOT_STARTED is derived, never stored), so the result is safe to feed persistStageDelta.
function diffCounts (before, after) {
  const delta = {}
  const ids = new Set([...Object.keys(before || {}), ...Object.keys(after || {})])
  for (const id of ids) {
    const d = countOf(after, id) - countOf(before, id)
    if (d) delta[id] = d
  }
  return delta
}

// Stage-count delta for marking |n| parts "begun" (n > 0) or un-begun (n < 0). Begun = the FIRST
// stage; parts come from / return to NOT_STARTED. This is the stage-aware replacement for the
// legacy `qtyPrinting += n` write — clamped to the notStarted pool / first-stage count.
export function beginDelta (before, stages, quantity, n) {
  if (!Array.isArray(stages) || !stages.length || !n) return {}
  const firstId = stages[0].id
  const after = n > 0
    ? transfer(before, stages, quantity, NOT_STARTED, firstId, n)
    : transfer(before, stages, quantity, firstId, NOT_STARTED, -n)
  return diffCounts(before, after)
}

// Stage-count delta for marking |n| parts "done/counted" (n > 0) or un-counting (n < 0). Done = the
// LAST stage. Advancing uses countOneDone (pull from the prior stage, else bootstrap from
// NOT_STARTED); un-counting moves parts back to the prior bucket. Stage-aware replacement for the
// legacy `qtyCounted += n` write.
export function doneDelta (before, stages, quantity, n) {
  if (!Array.isArray(stages) || !stages.length || !n) return {}
  let after = { ...(before || {}) }
  if (n > 0) {
    for (let k = 0; k < n; k++) after = countOneDone(after, stages, quantity)
  } else {
    const lastId = stages[stages.length - 1].id
    const prev = stages.length >= 2 ? stages[stages.length - 2].id : NOT_STARTED
    after = transfer(after, stages, quantity, lastId, prev, -n)
  }
  return diffCounts(before, after)
}

// Advance one part INTO stage `i`: pull from the nearest occupied earlier stage (i-1, then i-2,
// … down to 0), else bootstrap from NOT_STARTED. The cascade matters when stages between the
// parts and the target are empty — e.g. marking the LAST stage while parts are still in Printing
// and the middle stage is empty: without it, the target never increments. For i = 0 there is no
// earlier stage, so it pulls from NOT_STARTED. No-op if nothing is available anywhere. Drives the
// build-level "mark as <stage>" action — deliberately distinct from planStageSet, which the
// per-part counter uses to *set* a stage's number.
export function countOneAt (counts, stages, quantity, i) {
  if (!Array.isArray(stages) || i < 0 || i >= stages.length) return { ...(counts || {}) }
  const hereId = stages[i].id
  let from = null
  for (let j = i - 1; j >= 0; j--) {
    if (countOf(counts, stages[j].id) > 0) { from = stages[j].id; break }
  }
  if (!from) from = notStartedCount(counts, stages, quantity) > 0 ? NOT_STARTED : null
  if (!from) return { ...(counts || {}) }
  return transfer(counts, stages, quantity, from, hereId, 1)
}

// Stage-count delta for marking |n| parts into stage `i` (n > 0) or back out of it (n < 0).
// Advancing uses countOneAt (prior stage, else NOT_STARTED); un-advancing returns parts to the
// prior bucket (stage i-1, or NOT_STARTED for i = 0). i = 0 reduces to beginDelta and i = last
// reduces to doneDelta. Keyed by real stage ids only, so safe to feed persistStageDelta.
export function stageDelta (before, stages, quantity, i, n) {
  if (!Array.isArray(stages) || i < 0 || i >= stages.length || !n) return {}
  let after = { ...(before || {}) }
  if (n > 0) {
    for (let k = 0; k < n; k++) after = countOneAt(after, stages, quantity, i)
  } else {
    const hereId = stages[i].id
    const prev = i >= 1 ? stages[i - 1].id : NOT_STARTED
    after = transfer(after, stages, quantity, hereId, prev, -n)
  }
  return diffCounts(before, after)
}

// Group resolved workflows (one per linked part) by signature, returning each distinct
// workflow's FULL ordered stage names. Drives the build-level "mark as <stage>" menu: one group
// when all parts share a workflow (rendered as a flat stage list), several groups when they
// diverge (rendered as a submenu per workflow). Every stage — first, intermediates, last — is
// listed by name; nothing is dropped, so a build mixing a 3-stage and a 2-stage workflow shows
// both workflows in full rather than collapsing into one mixed list.
export function stageMarkGroups (resolvedWorkflows) {
  const groups = []
  const seen = new Set()
  for (const wf of (resolvedWorkflows || [])) {
    const stages = (wf && wf.stages) || []
    if (!stages.length) continue
    const names = stages.map(s => s.name)
    const sig = JSON.stringify(names)
    if (seen.has(sig)) continue
    seen.add(sig)
    groups.push({ name: (wf && wf.name) || '', stages: names })
  }
  return groups
}

// Per-model confirmation toast for a build mark, e.g. "LNA-42010 testmodel1.stl now has
// 5/10 Printing". `report` is { stageName, numerator, denominator } (see stageWrites). The
// numerator/denominator match the parts-list fraction so the toast agrees with the UI.
export function markToastMessage (projectLabel, modelName, report) {
  const who = [projectLabel, modelName].filter(Boolean).join(' ')
  return `${who} now has ${report.numerator}/${report.denominator} ${report.stageName}`
}

// {name, fields, text} for a build-level mark-action history event (Feature B). `kind` is
// 'printing' | 'counted' | 'stage' | 'failure'; `stageName` is required for 'stage'. Quantity
// is reported absolute — events log the press/intent, not the signed stage delta.
export function buildMarkEvent (kind, { stageName, quantity, buildName } = {}) {
  const qty = Math.abs(parseInt(quantity || 0) || 0)
  if (kind === 'failure') {
    return {
      name: 'Recorded a Failure',
      fields: { Build: buildName, Quantity: qty },
      text: `Recorded a failure of ${qty} part(s) — build "${buildName}".`,
    }
  }
  const stage = kind === 'printing' ? 'Printing' : kind === 'counted' ? 'Counted' : stageName
  return {
    name: `Marked as ${stage}`,
    fields: { Build: buildName, Quantity: qty, Stage: stage },
    text: `Marked ${qty} part(s) as ${stage} — build "${buildName}".`,
  }
}

const hasLegacyCounts = (model) =>
  (parseInt(model && model.qtyPrinting) || 0) > 0 || (parseInt(model && model.qtyCounted) || 0) > 0

// --- Per-part tracking (formfactories-vue3) -------------------------------------------------
// A per-part model's individual units live in enterprises/{e}/projects/{p}/modelUnits/{uuid}, and
// a Cloud Function derives modelData[uuid].stageCounts, model.quantity and
// modelData[uuid].unitsPriceTotal from them. Those fields are therefore backend-owned: the
// security rules reject a client stageCounts write for such a model, and a quantity edit here is
// overwritten on the next unit write. The legacy app keeps DISPLAYING the derived counters and
// only stops offering the edits. See the per-part tracking design spec, section 11.
export const PER_PART_LEGACY_MESSAGE = 'Per-part tracking is not supported in the Legacy App'

// `project` is a project document (or any object with a modelData map).
export function isPerPartModel (project, modelUUID) {
  return ((project && project.modelData && project.modelData[modelUUID]) || {}).perPart === true
}

// The sum of the units' effective prices, recomputed by the Cloud Function on every modelUnits
// write. A unit may carry a priceOverride, so this — not `unit price x quantity` — is a per-part
// model's line total. Null when the model is not per-part, or when the backend has not written
// the field yet (the caller then falls back to the ordinary multiplication).
export function unitsPriceTotal (project, modelUUID) {
  if (!isPerPartModel(project, modelUUID)) return null
  const total = project.modelData[modelUUID].unitsPriceTotal
  return (typeof total === 'number' && isFinite(total)) ? total : null
}

// True when a stageCounts map holds at least one part in any stage.
export function hasProgress (counts) {
  return !!counts && Object.values(counts).some(c => (parseInt(c) || 0) > 0)
}

// A model's workflow snapshot (modelData[uuid].workflow) is only the source of truth while the
// part has real progress (some stage count > 0) or was explicitly customized in the workflow
// dialog (workflowCustom). Otherwise the snapshot is an incidental leftover from count-tracking
// and we re-resolve the workflow live from the part's current settings — so a part whose counts
// are back to 0 follows technology/material/color changes again, like a fresh part.
export function snapshotIsAuthoritative (modelData) {
  return !!modelData && (!!modelData.workflowCustom || hasProgress(modelData.stageCounts))
}

const HARDCODED_LEGACY = {
  name: 'Legacy',
  stages: [
    { id: 'legacy-printing', name: 'Printing', color: '#90caf9' },
    { id: 'legacy-counted', name: 'Counted', color: '#a5d6a7' },
  ],
}

// A model's requestedSettings store catalog *names* (e.g. Technology "SLS"), but the workflow
// override buckets are keyed by catalog *UUIDs* (see CatalogSectionWorkflows.createOverride /
// overrideTargetKey). Translate names -> UUIDs via catalogByName so resolveWorkflow's lookups
// match. Falls back to the raw value when catalog/entry is unavailable (also lets a caller pass
// UUIDs directly). `catalog` is `$root.catalogByName`.
export function settingsToUUIDs (requestedSettings, catalog) {
  const s = requestedSettings || {}
  const toId = (level, value) => {
    if (!value || !catalog) return value
    const children = catalog[level] && catalog[level].children
    const child = children && children[value]
    return (child && child.uuid) || value
  }
  return {
    Technology: toId('Technology', s.Technology),
    Material: toId('Material', s.Material),
    Color: toId('Color', s.Color),
  }
}

export function getModelWorkflow (project, model, cfg, catalog) {
  const data = (project && project.modelData && project.modelData[model.uuid]) || {}
  if (data.workflow && Array.isArray(data.workflow.stages) && data.workflow.stages.length && snapshotIsAuthoritative(data)) {
    return { workflow: data.workflow, isSnapshot: true }
  }
  if (!data.stageCounts && hasLegacyCounts(model)) {
    return { workflow: (cfg && cfg.legacyWorkflow) || HARDCODED_LEGACY, isSnapshot: false, isLegacy: true }
  }
  // requestedSettings hold catalog names; map to UUIDs (the override keys) when a catalog is given.
  const settings = catalog ? settingsToUUIDs(model.requestedSettings, catalog) : model.requestedSettings
  // Never return a null/empty workflow: resolveWorkflow yields undefined when cfg is missing
  // (e.g. during the async workflowsConfig load), which would crash consumers that read .stages.
  const resolved = resolveWorkflow(settings, cfg)
  const workflow = (resolved && Array.isArray(resolved.stages) && resolved.stages.length) ? resolved : HARDCODED_LEGACY
  return { workflow, isSnapshot: false }
}

export function getStageCounts (project, model, workflow) {
  const data = (project && project.modelData && project.modelData[model.uuid]) || {}
  if (data.stageCounts && typeof data.stageCounts === 'object') return data.stageCounts
  return migrateLegacyCounters(workflow, model)
}

export function firstStageCount (project, model, cfg, catalog) {
  const { workflow } = getModelWorkflow(project, model, cfg, catalog)
  const stages = (workflow && workflow.stages) || []
  const counts = getStageCounts(project, model, workflow)
  return numeratorAt(counts, stages, 0)
}

export function lastStageCount (project, model, cfg, catalog) {
  const { workflow } = getModelWorkflow(project, model, cfg, catalog)
  const stages = (workflow && workflow.stages) || []
  const counts = getStageCounts(project, model, workflow)
  return numeratorAt(counts, stages, stages.length - 1)
}

// Project-level per-stage progress chips. Each part resolves its own workflow (parts on different
// technologies/materials can have different stage sets), so we aggregate by stage NAME — every
// part's "Printing" count folds into one Printing chip. numerator/denominator reuse the per-stage
// numeratorAt/denominatorAt math (denominator = quantity − Σ later stages), summed across parts.
// Chip order follows the longest part workflow (its stage list is the "spine"); names not on the
// spine are appended in first-seen order. Returns [{ name, numerator, denominator }], [] for no parts.
export function projectStageChips (project, models, cfg, catalog) {
  const list = Array.isArray(models) ? models : []
  const byKey = new Map() // lowercased name -> { name, numerator, denominator, firstSeen }
  let spine = [] // stage list of the longest workflow encountered
  let seq = 0

  for (const model of list) {
    const { workflow } = getModelWorkflow(project, model, cfg, catalog)
    const stages = (workflow && workflow.stages) || []
    if (!stages.length) continue
    if (stages.length > spine.length) spine = stages
    const counts = getStageCounts(project, model, workflow)
    const quantity = parseInt((model && model.quantity) || 0) || 0
    stages.forEach((stage, i) => {
      const key = String((stage && stage.name) || '').trim().toLowerCase()
      const bucket = byKey.get(key) || { name: stage.name, numerator: 0, denominator: 0, firstSeen: seq++ }
      bucket.numerator += numeratorAt(counts, stages, i)
      bucket.denominator += denominatorAt(counts, stages, i, quantity)
      byKey.set(key, bucket)
    })
  }

  const spineOrder = new Map()
  spine.forEach((stage, i) => {
    const key = String((stage && stage.name) || '').trim().toLowerCase()
    if (!spineOrder.has(key)) spineOrder.set(key, i)
  })
  const rank = (key, firstSeen) => spineOrder.has(key)
    ? spineOrder.get(key)
    : spine.length + firstSeen // off-spine names trail the spine, in first-seen order

  return [...byKey.entries()]
    .sort((a, b) => rank(a[0], a[1].firstSeen) - rank(b[0], b[1].firstSeen))
    .map(([, b]) => ({ name: b.name, numerator: b.numerator, denominator: b.denominator }))
}

// Build a Firestore field-path update object of increment() transforms from a before/after diff.
// `inc` is the firestore `increment` fn (injected for testability).
export function stageCountUpdate (modelUUID, before, after, inc) {
  const update = {}
  const ids = new Set([...Object.keys(before || {}), ...Object.keys(after || {})])
  for (const id of ids) {
    const delta = countOf(after, id) - countOf(before, id)
    if (delta !== 0) update[`modelData.${modelUUID}.stageCounts.${id}`] = inc(delta)
  }
  return update
}

// All distinct catalog stage names (default + hidden legacy + every by-tech/material/color
// override), sorted. Mirrors the backend computeActiveStages whitelist so the dashboard "stage"
// filter only offers names the backend actually indexes into Project.activeStages.
export function catalogStageNames (config) {
  // A freshly ported enterprise has no config/workflows doc, so workflowsConfig is null (not just
  // missing a legacyWorkflow). Don't short-circuit to [] — fall through so the hardcoded
  // 'Printing'/'Counted' legacy fallback below still applies, matching legacyStageNames/
  // getModelWorkflow, which resolve old projects to those same stages regardless of config.
  const cfg = config || {}
  const workflows = [
    cfg.defaultWorkflow,
    cfg.legacyWorkflow || HARDCODED_LEGACY,
    ...Object.values(cfg.byTechnology || {}),
    ...Object.values(cfg.byMaterial || {}),
    ...Object.values(cfg.byColor || {}),
  ]
  const names = new Set()
  workflows.forEach(wf => {
    if (!wf || !Array.isArray(wf.stages)) return
    wf.stages.forEach(s => { if (s && s.name) names.add(s.name) })
  })
  return [...names].sort()
}

// The legacy qtyPrinting/qtyCounted counters map onto the legacy workflow's first (begun)
// and last (done) stage names — the same mapping migrateLegacyCounters uses (qtyPrinting -
// qtyCounted -> first stage, qtyCounted -> last stage). Returns those two names so the
// dashboard can fold old projects (no stageCounts/activeStages) into a filter on those
// stages. Defaults to HARDCODED_LEGACY ('Printing'/'Counted') when no legacyWorkflow is set.
export function legacyStageNames (config) {
  const wf = (config && config.legacyWorkflow) || HARDCODED_LEGACY
  const stages = (wf && wf.stages) || []
  if (!stages.length) return { begun: null, done: null }
  return { begun: stages[0].name, done: stages[stages.length - 1].name }
}

export function createSeedWorkflowsConfig (lastUpdatedBy, now, makeUUID) {
  const twoStage = () => ({
    name: 'Default',
    stages: [
      { id: makeUUID(), name: 'Printing', color: '#90caf9' },
      { id: makeUUID(), name: 'Counted', color: '#a5d6a7' },
    ],
  })
  const legacy = twoStage()
  legacy.name = 'Legacy'
  return {
    dateModified: now,
    lastUpdatedBy,
    defaultWorkflow: twoStage(),
    byTechnology: {},
    byMaterial: {},
    byColor: {},
    legacyWorkflow: legacy,
  }
}
